import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import AdatokPanel, { type LeadSor } from '../leadek/AdatokPanel.tsx';
import Atosztas from '../leadek/Atosztas.tsx';
import BecslesPanel from '../leadek/BecslesPanel.tsx';
import LevezetesPanel from '../leadek/LevezetesPanel.tsx';
import MinosegPanel from '../leadek/MinosegPanel.tsx';
import HivasGombok from '../leadek/HivasGombok.tsx';
import HivasSzkript from '../leadek/HivasSzkript.tsx';
import Idovonal from '../leadek/Idovonal.tsx';
import JegyzetUrlap from '../leadek/JegyzetUrlap.tsx';
import LeadTorles from '../leadek/LeadTorles.tsx';
import StatuszValto from '../leadek/StatuszValto.tsx';
import { formatDatumIdo, telefonMegjelenites } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, HibaUzenet, Kartya, StatuszJelveny, Ures } from '../ui/elemek.tsx';
import { IDOSAV_CIMKE, type Idosav } from '../../../web/src/lib/cimkek.ts';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function NincsHozzaferes() {
  return (
    <Ures cim="Nincs ilyen lead, vagy nincs hozzá hozzáférésed." szoveg="Ellenőrizd a linket, vagy térj vissza a leadlistához.">
      <div data-testid="nincs-hozzaferes">
        <Link to="/leadek" className="font-semibold text-kiemelt underline">Vissza a leadekhez</Link>
      </div>
    </Ures>
  );
}

export default function LeadAdatlap() {
  const { id = '' } = useParams();
  const { jogok } = useAktivIroda();
  const idErvenyes = UUID.test(id);
  // Melyik lead státuszmentése sikerült utoljára (a StatuszValto státuszváltáskor újra felépül, ezért itt él).
  const [mentveLead, setMentveLead] = useState<string | null>(null);
  const lead = useQuery({
    queryKey: ['lead', id],
    enabled: idErvenyes,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('*, terulet:teruletek(nev), kollega:profiles!leads_assigned_to_fkey(nev, email), iroda:offices(nev, slug)')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as LeadSor | null;
    },
  });

  if (!idErvenyes) return <NincsHozzaferes />;
  if (lead.isPending) return <Betolto />;
  if (lead.error) return <HibaUzenet hiba={lead.error} />;
  const l = lead.data;
  if (!l) return <NincsHozzaferes />;

  const idosav = l.hivasi_idosav ? IDOSAV_CIMKE[l.hivasi_idosav as Idosav] ?? l.hivasi_idosav : null;
  return (
    <div className="grid min-w-0 grid-cols-1 gap-4">
      <Link to="/leadek" className="inline-flex min-h-10 w-fit items-center text-sm text-szoveg-halk hover:text-szoveg">Vissza a leadekhez</Link>
      <Kartya>
        <div className="grid min-w-0 grid-cols-1 gap-5">
          <div className="grid min-w-0 gap-1.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <h1 className="wrap-anywhere text-2xl font-medium tracking-[-0.025em] md:text-3xl">{l.nev}</h1>
              <StatuszJelveny statusz={l.statusz} />
            </div>
            <p className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-szoveg-halk">
              <span>Beérkezett: <span className="szam">{formatDatumIdo(l.created_at)}</span></span>
              {jogok.super && l.iroda && <span>{l.iroda.nev}</span>}
              <span>{l.kollega ? `Felelős: ${l.kollega.nev}` : 'Nincs felelős'}</span>
            </p>
            <a href={`tel:${l.telefon}`} className="szam-nagy mt-2 w-fit text-3xl hover:text-kiemelt">{telefonMegjelenites(l.telefon)}</a>
            {idosav && <p className="text-sm text-szoveg-halk">Hívható: {idosav}</p>}
          </div>
          {/* A gyorsgombok csukva indulnak: a lead kezelése a Státusz kártyán történik (2026-10-29). */}
          <details data-testid="hivas-eszkozok" className="group rounded-xl border border-vonal">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3.5 text-sm text-szoveg-halk hover:text-szoveg">
              Hívás, e-mail, hívás eredményének rögzítése
              <span aria-hidden className="transition group-open:rotate-180">▾</span>
            </summary>
            <div className="grid gap-4 border-t border-vonal p-3.5">
              <div className="grid gap-2 sm:grid-cols-[2fr_1fr]">
                <a href={`tel:${l.telefon}`} className="inline-flex min-h-12 items-center justify-center rounded-xl border border-vonal-eros px-4 text-szoveg transition hover:bg-felulet">
                  Hívás
                </a>
                <a href={`mailto:${l.email}`} className="inline-flex min-h-12 items-center justify-center rounded-xl border border-vonal-eros px-4 text-szoveg transition hover:bg-felulet">
                  E-mail
                </a>
              </div>
              {l.statusz !== 'nem_ellenorzott' && <HivasGombok leadId={l.id} officeId={l.office_id} />}
            </div>
          </details>
        </div>
      </Kartya>
      <StatuszValto key={`${l.id}-${l.statusz}`} lead={l} mentve={mentveLead === l.id} onMentve={(m) => setMentveLead(m ? l.id : null)} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div className="grid min-w-0 grid-cols-1 gap-4">
          <HivasSzkript lead={l} />
          <Idovonal leadId={l.id} />
          <JegyzetUrlap leadId={l.id} officeId={l.office_id} />
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-4">
          <BecslesPanel lead={l} />
          <LevezetesPanel lead={l} />
          <AdatokPanel lead={l} />
          {jogok.super && <MinosegPanel leadId={l.id} />}
          {jogok.vezeto && <Atosztas leadId={l.id} officeId={l.office_id} felelos={l.assigned_to} />}
        </div>
      </div>
      {jogok.super && <LeadTorles lead={l} />}
    </div>
  );
}
